import Bugsnag from "@bugsnag/js";
import { createBrowserHistory } from "history";
import { FunctionComponent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import ButtonSmily from "@Components/atoms/ButtonSmily";
import Link from "@Components/atoms/Link";
import BottomSheet from "@Components/molecules/BottomSheet";
import ChoicesPacks from "@Components/molecules/choices/ChoicesPacks";
import { useGlobalLoader } from "@Components/WithGlobalLoader";
import WithLocale from "@Components/WithLocale";
import { ReactComponent as IconPhone } from "@Icons/colored/phone.svg";
import CheerzApiProvider from "@Providers/CheerzApiProvider";
import { ApiV2ErrorResponse } from "@Types/api/error";
import { Page, Tile } from "@Types/pagify";
import { SettingsContent } from "@Types/settings";
import User from "@Types/user";
import getUrlAppStoreButton from "@Utils/appStoreUrl";
import { apiV2ErrorManager } from "@Utils/error";
import { getCsrfToken } from "@Utils/meta";
import { convertPageFromApiToContent, getLink } from "@Utils/pagify";
import { convertSettingsFromApiToContent } from "@Utils/settings";
import { setDataLayerMandatoryPageTrackers } from "@Utils/track";
import "./style.scss";

const history = createBrowserHistory();

interface Props {
  availableWebMobile: boolean | undefined;
  tile: Tile | undefined;
}

interface ContainerProps {
  userData: User;
  productTag: string;
}

export const DownloadApp: FunctionComponent<Props> = ({ availableWebMobile, tile }) => {
  const intl = useIntl();
  const [displayPacks, setDiplayPacks] = useState<boolean>(false);

  if (!tile) {
    Bugsnag.notify(new Error("tile for section product_infos_web not found in DownloadApp"));
    return null;
  }

  const link = getLink(tile.navigationLink, intl.locale);

  const handleClickApp = () => {
    window.location.href = getUrlAppStoreButton();
  };

  const handleClickWeb = () => {
    if (
      tile.action?.entries &&
      tile.action.entries.length > 0 &&
      tile.metadata?.kind === "product_only_with_packs"
    ) {
      setDiplayPacks(true);
    } else {
      setDiplayPacks(false);
      if (!link) {
        Bugsnag.notify(new Error(`navigationLink for tile ${tile.slug} not found in DownloadApp`));
        return;
      }
      location.href = link;
    }
  };

  return (
    <div className="DownloadApp">
      <IconPhone />
      <div className="DownloadApp-title">
        {intl.formatMessage({ id: "cheerz_DownloadApp_title" })}
      </div>
      <ButtonSmily onClick={() => handleClickApp()} size="s" type="primary">
        {intl.formatMessage({ id: "cheerz_DownloadApp_app" })}
      </ButtonSmily>
      {availableWebMobile && (
        <>
          <Link onClick={handleClickWeb} size="m">
            {intl.formatMessage({ id: "cheerz_DownloadApp_web" })}
          </Link>
          <BottomSheet
            display={displayPacks}
            onDismiss={() => setDiplayPacks(false)}
            showCloseButton={true}
          >
            <ChoicesPacks tile={tile} />
          </BottomSheet>
        </>
      )}
    </div>
  );
};

const DownloadAppContainer: FunctionComponent<ContainerProps> = ({ productTag, userData }) => {
  const [product, setProduct] = useState<Page>();
  const [tile, setTile] = useState<Tile>();
  const [availableWebMobile, setAvailableWebMobile] = useState<boolean>(false);
  const [settings, setSettings] = useState<SettingsContent>();
  const { setLoading } = useGlobalLoader();
  const intl = useIntl();
  const locale = intl.locale;

  const productSlug = `product_page_web-${productTag}`;

  const cheerzApiProvider = new CheerzApiProvider(locale, getCsrfToken(), true);

  useEffect(() => {
    cheerzApiProvider
      .getSettings(locale)
      .then((settingsData) => {
        setSettings(convertSettingsFromApiToContent(settingsData.response));
        return cheerzApiProvider.getPage(productSlug, settingsData.response).then((data) => {
          setProduct(convertPageFromApiToContent(data.response));
          setLoading(false);
        });
      })
      .catch((error: ApiV2ErrorResponse) => apiV2ErrorManager(error));
  }, []);

  useEffect(() => {
    if (product && settings) {
      setDataLayerMandatoryPageTrackers(
        settings?.currency,
        settings?.lang,
        productTag,
        "DownloadApp",
        window.location.pathname,
        userData
      );
    }
  }, [product, settings]);

  useEffect(() => {
    if (product) {
      const productInfosSection = product.sections.find(
        (section) => section.displayType === "product_infos_web"
      );
      setTile(product.tiles.find((tile) => new Set(productInfosSection?.tileSlugs).has(tile.slug)));
      setAvailableWebMobile(product.metadata?.kustomWebMobile || false);
    }
  }, [product]);

  return (
    <WithLocale history={history}>
      {product && <DownloadApp availableWebMobile={availableWebMobile} tile={tile} />}
    </WithLocale>
  );
};

export default DownloadAppContainer;
